import { useEffect, useState } from "react";
import { LifeBuoy, Mail, Loader2, CheckCircle2, Clock, Circle, Copy, Send, X } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

const STATUS_STYLES = {
  new: { bg: "bg-rose-50 text-rose-700 border-rose-200", icon: Circle, label: "New" },
  in_progress: { bg: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, label: "In progress" },
  resolved: { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Resolved" },
};

export default function AdminSupport() {
  const [data, setData] = useState({ messages: [], counts: { new: 0, in_progress: 0, resolved: 0, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  // Iter42: in-app reply composer state — one open at a time.
  const [replyingTo, setReplyingTo] = useState(null); // message obj
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : "";
      const r = await adminApi.get(`/admin/support/messages${q}`);
      setData(r.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("support inbox fetch failed:", err);
      toast.error("Couldn't load support messages");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await adminApi.patch(`/admin/support/messages/${id}`, { status });
      toast.success(`Marked as ${status.replace("_", " ")}`);
      await load();
    } catch {
      toast.error("Update failed");
    } finally { setUpdatingId(null); }
  };

  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`Copied ${email}`);
    } catch {
      // Fallback for very old browsers / iframes without clipboard access.
      const el = document.createElement("textarea");
      el.value = email;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); toast.success(`Copied ${email}`); }
      catch { toast.error("Copy failed. Please copy manually."); }
      document.body.removeChild(el);
    }
  };

  const openReply = (m) => {
    setReplyingTo(m);
    setReplyBody(`Hi ${(m.from_name || "").split(" ")[0] || "there"},\n\n`);
  };

  const closeReply = () => {
    setReplyingTo(null);
    setReplyBody("");
  };

  const sendReply = async () => {
    if (!replyingTo || !replyBody.trim()) {
      toast.error("Reply body is required");
      return;
    }
    setSending(true);
    try {
      const r = await adminApi.post(`/admin/support/messages/${replyingTo.id}/reply`, { body: replyBody.trim() });
      toast.success(`Reply sent to ${r.data.sent_to}`);
      closeReply();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed. Try Copy email as fallback.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="admin-support">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-kudi-terracotta" /> Support inbox
          </h1>
          <p className="text-sm text-kudi-ink/60 mt-1">Every message submitted via the SupportWidget or landing footer.</p>
        </div>
      </div>

      {/* Stat pills */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { key: "", label: "All", n: data.counts.total, tone: "bg-white text-kudi-ink border-border" },
          { key: "new", label: "New", n: data.counts.new, tone: "bg-rose-50 text-rose-700 border-rose-200" },
          { key: "in_progress", label: "In progress", n: data.counts.in_progress, tone: "bg-amber-50 text-amber-700 border-amber-200" },
          { key: "resolved", label: "Resolved", n: data.counts.resolved, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
        ].map((s) => (
          <button
            key={s.key || "all"}
            onClick={() => setFilter(s.key)}
            className={`p-3 rounded-xl border text-left transition ${s.tone} ${filter === s.key ? "ring-2 ring-offset-1 ring-kudi-terracotta" : "hover:opacity-90"}`}
            data-testid={`filter-${s.key || "all"}`}
          >
            <div className="text-xs opacity-80">{s.label}</div>
            <div className="text-2xl font-bold tabular-nums">{s.n}</div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-kudi-ink/40 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading messages…
          </div>
        ) : data.messages.length === 0 ? (
          <div className="p-8 text-center">
            <Mail className="w-10 h-10 mx-auto text-kudi-ink/30 mb-2" />
            <div className="text-kudi-ink/50">No support messages{filter ? ` in "${filter.replace("_", " ")}"` : ""}.</div>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {data.messages.map((m) => {
              const s = STATUS_STYLES[m.status] || STATUS_STYLES.new;
              const S = s.icon;
              return (
                <li key={m.id} className="p-4 hover:bg-accent/40 transition" data-testid={`support-row-${m.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border ${s.bg}`}>
                      <S className="w-3 h-3" /> {s.label}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-kudi-ink truncate">
                        {m.from_name || "Anonymous"} <span className="text-kudi-ink/50 font-normal">· {m.from_email}</span>
                      </div>
                      <div className="text-xs text-kudi-ink/50 mt-0.5">
                        {new Date(m.created_at).toLocaleString("en-NG")}
                        {m.page && <> · <span className="font-mono">{m.page}</span></>}
                        {m.subject && <> · Subject: <span className="font-medium">{m.subject}</span></>}
                        {m.email_dispatched === false && <span className="ml-2 text-rose-600">· email failed</span>}
                      </div>
                      <div className="mt-2 text-sm whitespace-pre-wrap break-words">{m.message}</div>
                      {m.admin_note && (
                        <div className="mt-2 text-xs bg-kudi-cream border-l-2 border-kudi-terracotta pl-2 py-1 text-kudi-ink/70">
                          <span className="font-semibold">Admin note:</span> {m.admin_note}
                        </div>
                      )}
                      {Array.isArray(m.admin_replies) && m.admin_replies.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                          {m.admin_replies.map((rep) => (
                            <div key={rep.id} className="text-xs bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-1.5" data-testid={`admin-reply-${rep.id}`}>
                              <div className="text-[10px] uppercase tracking-wider text-emerald-700/70 font-semibold">
                                Sent {new Date(rep.at).toLocaleString("en-NG")} by {rep.by}
                              </div>
                              <div className="text-kudi-ink/80 whitespace-pre-wrap mt-0.5">{rep.body}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0 items-end">
                      <button
                        onClick={() => openReply(m)}
                        className="text-xs bg-kudi-green text-white px-3 py-1.5 rounded-lg hover:bg-kudi-green-dark transition inline-flex items-center gap-1"
                        data-testid={`reply-${m.id}`}
                      >
                        <Send className="w-3 h-3" /> Reply
                      </button>
                      <button
                        onClick={() => copyEmail(m.from_email)}
                        className="text-xs bg-white border border-border text-kudi-ink px-3 py-1.5 rounded-lg hover:bg-accent/40 transition inline-flex items-center gap-1"
                        data-testid={`copy-email-${m.id}`}
                        title={m.from_email}
                      >
                        <Copy className="w-3 h-3" /> Copy email
                      </button>
                      <a
                        href={`mailto:${m.from_email}?subject=${encodeURIComponent("Re: " + (m.subject || "Kobolog support"))}&body=${encodeURIComponent("Hi " + (m.from_name || "there") + ",\n\n")}`}
                        className="text-[10px] text-kudi-ink/50 hover:text-kudi-ink underline"
                        data-testid={`mailto-${m.id}`}
                      >
                        Open mail app
                      </a>
                      <select
                        value={m.status || "new"}
                        onChange={(e) => updateStatus(m.id, e.target.value)}
                        disabled={updatingId === m.id}
                        className="text-xs border border-border rounded-lg px-2 py-1.5 bg-white hover:border-kudi-terracotta focus:border-kudi-terracotta focus:outline-none disabled:opacity-50 mt-1"
                        data-testid={`status-${m.id}`}
                      >
                        <option value="new">New</option>
                        <option value="in_progress">In progress</option>
                        <option value="resolved">Resolved</option>
                      </select>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Iter42: in-app reply composer (Brevo) */}
      {replyingTo && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={closeReply}
          data-testid="reply-composer-backdrop"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="reply-composer"
          >
            <div className="p-4 border-b border-border flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-kudi-ink/50 font-semibold">Reply via Brevo</div>
                <div className="text-sm font-semibold truncate">
                  To: {replyingTo.from_name ? `${replyingTo.from_name} · ` : ""}
                  <span className="text-kudi-ink/70">{replyingTo.from_email}</span>
                </div>
                {replyingTo.subject && (
                  <div className="text-xs text-kudi-ink/50 truncate">Re: {replyingTo.subject}</div>
                )}
              </div>
              <button onClick={closeReply} className="shrink-0 w-8 h-8 rounded-full hover:bg-accent/40 flex items-center justify-center" data-testid="reply-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                rows={8}
                autoFocus
                placeholder="Type your reply. It will be sent from the Kobolog support address via Brevo."
                className="w-full border border-border rounded-xl p-3 text-sm focus:border-kudi-green focus:outline-none focus:ring-2 focus:ring-kudi-green/30 resize-none"
                data-testid="reply-body"
              />
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                <button
                  onClick={() => copyEmail(replyingTo.from_email)}
                  className="text-xs bg-white border border-border text-kudi-ink px-3 py-2 rounded-lg hover:bg-accent/40 transition inline-flex items-center gap-1"
                  data-testid="reply-copy-email"
                >
                  <Copy className="w-3 h-3" /> Copy email
                </button>
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={closeReply}
                    className="text-sm px-3 py-2 rounded-lg hover:bg-accent/40 transition"
                    disabled={sending}
                    data-testid="reply-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendReply}
                    disabled={sending || !replyBody.trim()}
                    className="text-sm font-semibold bg-kudi-green text-white px-4 py-2 rounded-lg hover:bg-kudi-green-dark transition inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="reply-send"
                  >
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    {sending ? "Sending..." : "Send reply"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
